import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import ErrorIcon from '@components/icons/error'
import SuccessIcon from '@components/icons/success'
import vroomClient from '@util/vroom_client'
import { FormMessage } from '@components/pages/sms/sms_form_message'
import { SectionHeadline } from '@components/typography/section_headline'
import { TextField } from '@components/form/text_field'
import { withGlobalContext } from '@util/global_context'

import styles from '@components/pages/sms/sms_form.module.scss'
import formMessageStyles from '@components/pages/sms/sms_form_message.module.scss'

const formatPhoneNumber = phoneNumber => {
  const digits = phoneNumber.replace(/\D/g, '')
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const SmsFormComponent = ({ globalContext }) => {
  const { t, i18n } = useTranslation()
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm()

  const { referrer } = globalContext

  const onSubmit = async values => {
    setShowSuccessMessage(false)
    setShowErrorMessage(false)

    const { phoneNumber, zipCode } = values
    const lang = i18n.language === 'es' ? 'ESP' : 'ENG'

    const result = await vroomClient.registerSignalVineUser({
      phone: formatPhoneNumber(phoneNumber),
      zip_code: zipCode,
      language: lang,
      referrer: referrer || 'vroomweb',
    })

    if (result.ok) {
      setShowSuccessMessage(true)
      reset()
    } else {
      setShowErrorMessage(true)
    }
  }

  return (
    <div className={styles.sms_form}>
      <SectionHeadline classes={styles.sms_form__section_headline}>
        {t('sms.getStarted')}
      </SectionHeadline>
      <SectionHeadline classes={styles.sms_form__section_subheadline}>
        {t('sms.availableLanguages')}
      </SectionHeadline>
      {showSuccessMessage && (
        <FormMessage
          message={t('sms.successMessage')}
          colorClass={formMessageStyles['form_message--success']}
        >
          <SuccessIcon />
        </FormMessage>
      )}
      {showErrorMessage && (
        <FormMessage
          message={t('sms.errorMessage')}
          colorClass={formMessageStyles['form_message--error']}
        >
          <ErrorIcon />
        </FormMessage>
      )}
      <div className="margin-top-3">
        <form id="smsForm" className={styles.sms_form__form} onSubmit={handleSubmit(onSubmit)}>
          <TextField
            type="tel"
            id="phoneNumber"
            label="Phone number"
            name="phoneNumber"
            error={Object.prototype.hasOwnProperty.call(errors, 'phoneNumber')}
            helperText={errors.phoneNumber && 'This value seems to be invalid.'}
            fullWidth
            {...register('phoneNumber', {
              required: true,
              validate: value => value.replace(/\D/g, '').length === 10,
            })}
          />
          <div className="margin-top-3">
            <TextField
              type="number"
              id="zipCode"
              label="Zip code"
              name="zipCode"
              error={Object.prototype.hasOwnProperty.call(errors, 'zipCode')}
              helperText={errors.zipCode && 'This value seems to be invalid.'}
              fullWidth
              {...register('zipCode', { required: true, minLength: 5, maxLength: 5 })}
            />
          </div>
          <div className={styles.sms_form__helper_text}>* {t('sms.messageRates')}</div>
          <div className={styles.sms_form__submit_field}>
            <button className={styles.sms_form__submit_button} name="submit" type="submit">
              Sign up
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const SmsForm = withGlobalContext(SmsFormComponent)

export { SmsForm }
